import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { usePubSub, useMeeting } from "@videosdk.live/react-sdk";
import logger from '../utils/logger';

const BreakoutRoomsContext = createContext();

export const useBreakoutRooms = () => useContext(BreakoutRoomsContext);

export const BreakoutRoomsProvider = ({ children }) => {
  const mMeeting = useMeeting();
  const localParticipantId = mMeeting?.localParticipant?.id;

  // State for rooms: { [roomId]: { id, name, participants: [participantIds], createdAt } }
  const [rooms, setRooms] = useState({});
  // State for participant room assignments: { [participantId]: roomId | null }
  const [participantRooms, setParticipantRooms] = useState({});
  // State for join requests: { [roomId]: [{ participantId, participantName, requestedAt }] }
  const [joinRequests, setJoinRequests] = useState({});
  // Current room the local participant is in
  const [currentRoom, setCurrentRoom] = useState(null);

  const roomsRef = useRef({});
  const participantRoomsRef = useRef({});

  // Update refs when state changes
  useEffect(() => {
    roomsRef.current = rooms;
    participantRoomsRef.current = participantRooms;
  }, [rooms, participantRooms]);

  // PubSub hooks for different message types
  const { publish: publishBreakoutMessage } = usePubSub("BREAKOUT_ROOMS", {
    onMessageReceived: (data) => {
      if (data.senderId === localParticipantId) return;

      try {
        const rawMessage = data.message || data;
        const message = typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
        const { type, payload } = message;

        handleBreakoutMessage(type, payload, data.senderId);
      } catch (error) {
        logger("error", "Error handling breakout room message:", error);
      }
    },
    onOldMessagesReceived: async (messages) => {
      // Restore state from old messages
      const currentRooms = {};
      const currentParticipantRooms = {};

      messages.forEach((msgData) => {
        try {
          const rawMessage = msgData.message || msgData;
          const message = typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
          const { type, payload } = message;

          if (type === "addRoom") {
            const { room } = payload;
            if (room) {
              currentRooms[room.id] = room;
              // Initialize participants array if not present
              if (!currentRooms[room.id].participants) {
                currentRooms[room.id].participants = [];
              }
            }
          } else if (type === "addParticipant") {
            const { roomId, participantId } = payload;
            if (roomId && participantId) {
              if (!currentRooms[roomId]) {
                // If room doesn't exist, we might need to wait for it
                return;
              }
              if (!currentRooms[roomId].participants) {
                currentRooms[roomId].participants = [];
              }
              if (!currentRooms[roomId].participants.includes(participantId)) {
                currentRooms[roomId].participants.push(participantId);
              }
              currentParticipantRooms[participantId] = roomId;
            }
          } else if (type === "removeParticipant") {
            const { roomId, participantId } = payload;
            if (roomId && currentRooms[roomId]) {
              currentRooms[roomId].participants = currentRooms[roomId].participants.filter(
                (id) => id !== participantId
              );
              if (currentParticipantRooms[participantId] === roomId) {
                delete currentParticipantRooms[participantId];
              }
            }
          } else if (type === "deleteRoom") {
            const { roomId } = payload;
            if (roomId) {
              delete currentRooms[roomId];
              // Remove all participants from this room
              Object.keys(currentParticipantRooms).forEach((pid) => {
                if (currentParticipantRooms[pid] === roomId) {
                  delete currentParticipantRooms[pid];
                }
              });
            }
          }
        } catch (error) {
          logger("error", "Error processing old message:", error);
        }
      });

      setRooms(currentRooms);
      setParticipantRooms(currentParticipantRooms);

      // Set current room for local participant
      if (currentParticipantRooms[localParticipantId]) {
        setCurrentRoom(currentParticipantRooms[localParticipantId]);
      }
    },
  });

  const handleBreakoutMessage = useCallback((type, payload, senderId) => {
    switch (type) {
      case "addRoom":
        setRooms((prev) => {
          const newRooms = { ...prev };
          const { room } = payload;
          if (room) {
            newRooms[room.id] = {
              ...room,
              participants: room.participants || [],
            };
          }
          return newRooms;
        });
        break;

      case "addParticipant":
        const { roomId: addRoomId, participantId: addParticipantId } = payload;
        setRooms((prev) => {
          const newRooms = { ...prev };
          if (newRooms[addRoomId]) {
            newRooms[addRoomId] = {
              ...newRooms[addRoomId],
              participants: [
                ...(newRooms[addRoomId].participants || []),
                addParticipantId,
              ].filter((id, index, arr) => arr.indexOf(id) === index), // Remove duplicates
            };
          }
          return newRooms;
        });
        setParticipantRooms((prev) => ({
          ...prev,
          [addParticipantId]: addRoomId,
        }));
        if (addParticipantId === localParticipantId) {
          setCurrentRoom(addRoomId);
        }
        // Remove join request if this was a request acceptance
        setJoinRequests((prev) => {
          const newRequests = { ...prev };
          if (newRequests[addRoomId]) {
            newRequests[addRoomId] = newRequests[addRoomId].filter(
              (req) => req.participantId !== addParticipantId
            );
            if (newRequests[addRoomId].length === 0) {
              delete newRequests[addRoomId];
            }
          }
          return newRequests;
        });
        break;

      case "removeParticipant":
        const { roomId: removeRoomId, participantId: removeParticipantId } = payload;
        setRooms((prev) => {
          const newRooms = { ...prev };
          if (newRooms[removeRoomId]) {
            newRooms[removeRoomId] = {
              ...newRooms[removeRoomId],
              participants: (newRooms[removeRoomId].participants || []).filter(
                (id) => id !== removeParticipantId
              ),
            };
          }
          return newRooms;
        });
        setParticipantRooms((prev) => {
          const updated = { ...prev };
          if (updated[removeParticipantId] === removeRoomId) {
            delete updated[removeParticipantId];
          }
          return updated;
        });
        if (removeParticipantId === localParticipantId) {
          setCurrentRoom(null);
        }
        break;

      case "deleteRoom":
        const { roomId: deleteRoomId } = payload;
        
        logger("log", "🔴 Handling deleteRoom message for roomId:", deleteRoomId);
        logger("log", "🔴 Current participantRooms before deletion:", participantRoomsRef.current);
        logger("log", "🔴 Current local currentRoom:", currentRoom);
        
        // Check if local participant was in this room BEFORE removing from participantRooms
        const localWasInRoom = participantRoomsRef.current[localParticipantId] === deleteRoomId;
        const localCurrentRoomMatches = currentRoom === deleteRoomId;
        
        // Remove the room
        setRooms((prev) => {
          const newRooms = { ...prev };
          delete newRooms[deleteRoomId];
          return newRooms;
        });
        
        // Remove all participants from this room so they return to main meeting
        // Create a completely new object to ensure React detects the change
        setParticipantRooms((prev) => {
          const updated = {};
          let hasChanges = false;
          
          // Copy all participants NOT in the deleted room to the new object
          Object.keys(prev).forEach((pid) => {
            if (prev[pid] !== deleteRoomId) {
              updated[pid] = prev[pid];
            } else {
              hasChanges = true;
              logger("log", "🔴 Removing participant", pid, "from room", deleteRoomId);
            }
          });
          
          logger("log", "🔴 Updated participantRooms after deletion:", updated);
          return hasChanges ? updated : prev;
        });
        
        // Clear currentRoom if local participant was in the deleted room
        // This MUST happen after participantRooms is updated so the view switches to main meeting
        if (localWasInRoom || localCurrentRoomMatches) {
          logger("log", "🔴 Local participant was in deleted room, clearing currentRoom to return to main meeting");
          setCurrentRoom(null);
        }
        break;

      case "requestJoin":
        const { roomId: requestRoomId, participantName } = payload;
        setJoinRequests((prev) => {
          const newRequests = { ...prev };
          if (!newRequests[requestRoomId]) {
            newRequests[requestRoomId] = [];
          }
          // Check if request already exists
          const existingIndex = newRequests[requestRoomId].findIndex(
            (req) => req.participantId === senderId
          );
          if (existingIndex === -1) {
            newRequests[requestRoomId].push({
              participantId: senderId,
              participantName: participantName || "Participant",
              requestedAt: new Date().getTime(),
            });
          }
          return newRequests;
        });
        break;

      case "cancelRequest":
        const { roomId: cancelRequestRoomId } = payload;
        setJoinRequests((prev) => {
          const newRequests = { ...prev };
          if (newRequests[cancelRequestRoomId]) {
            newRequests[cancelRequestRoomId] = newRequests[cancelRequestRoomId].filter(
              (req) => req.participantId !== senderId
            );
            if (newRequests[cancelRequestRoomId].length === 0) {
              delete newRequests[cancelRequestRoomId];
            }
          }
          return newRequests;
        });
        break;

      default:
        break;
    }
  }, [localParticipantId, currentRoom]);

  // Host functions
  const createRoom = useCallback((roomName) => {
    const roomId = `room_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const room = {
      id: roomId,
      name: roomName,
      participants: [],
      createdAt: new Date().getTime(),
    };

    publishBreakoutMessage(
      JSON.stringify({
        type: "addRoom",
        payload: { room },
      }),
      { persist: true }
    );

    setRooms((prev) => ({
      ...prev,
      [roomId]: room,
    }));

    return roomId;
  }, [publishBreakoutMessage]);

  const deleteRoom = useCallback((roomId) => {
    logger("log", "🔴 Host deleting room:", roomId);
    logger("log", "🔴 Current rooms:", rooms);
    logger("log", "🔴 Current participantRooms before deletion:", participantRooms);
    
    // Get all participants in this room before deleting
    const room = rooms[roomId];
    const participantsInRoom = room?.participants || [];
    
    logger("log", "🔴 Participants in room to be deleted:", participantsInRoom);
    
    // Check if host/local participant was in this room BEFORE deleting
    const localWasInRoom = participantRooms[localParticipantId] === roomId;
    const localCurrentRoomMatches = currentRoom === roomId;
    
    publishBreakoutMessage(
      JSON.stringify({
        type: "deleteRoom",
        payload: { roomId, participants: participantsInRoom },
      }),
      { persist: true }
    );

    // Update local state immediately - remove the room
    setRooms((prev) => {
      const newRooms = { ...prev };
      delete newRooms[roomId];
      return newRooms;
    });

    // Remove all participants from this room so they return to main meeting
    // Create a completely new object to ensure React detects the change
    setParticipantRooms((prev) => {
      const updated = {};
      let hasChanges = false;
      
      // Copy all participants NOT in the deleted room to the new object
      Object.keys(prev).forEach((pid) => {
        if (prev[pid] !== roomId) {
          updated[pid] = prev[pid];
        } else {
          hasChanges = true;
          logger("log", "🔴 Host: Removing participant", pid, "from room", roomId);
        }
      });
      
      logger("log", "🔴 Host: Updated participantRooms after deletion:", updated);
      return hasChanges ? updated : prev;
    });

    // Clear currentRoom if local participant (host) was in the deleted room
    // This MUST happen after participantRooms is updated so the view switches to main meeting
    if (localWasInRoom || localCurrentRoomMatches) {
      logger("log", "🔴 Host: Clearing currentRoom because host was in deleted room - returning to main meeting");
      setCurrentRoom(null);
    }
  }, [publishBreakoutMessage, currentRoom, rooms, participantRooms, localParticipantId]);

  const addParticipantToRoom = useCallback((roomId, participantId) => {
    publishBreakoutMessage(
      JSON.stringify({
        type: "addParticipant",
        payload: { roomId, participantId },
      }),
      { persist: true }
    );

    setRooms((prev) => {
      const newRooms = { ...prev };
      if (newRooms[roomId]) {
        newRooms[roomId] = {
          ...newRooms[roomId],
          participants: [
            ...(newRooms[roomId].participants || []),
            participantId,
          ].filter((id, index, arr) => arr.indexOf(id) === index),
        };
      }
      return newRooms;
    });

    setParticipantRooms((prev) => ({
      ...prev,
      [participantId]: roomId,
    }));

    // If adding local participant to room, set currentRoom immediately
    // This ensures the host sees participants when they join a breakout room
    if (participantId === localParticipantId) {
      setCurrentRoom(roomId);
    }
  }, [publishBreakoutMessage, localParticipantId]);

  const removeParticipantFromRoom = useCallback((roomId, participantId) => {
    publishBreakoutMessage(
      JSON.stringify({
        type: "removeParticipant",
        payload: { roomId, participantId },
      }),
      { persist: true }
    );

    setRooms((prev) => {
      const newRooms = { ...prev };
      if (newRooms[roomId]) {
        newRooms[roomId] = {
          ...newRooms[roomId],
          participants: (newRooms[roomId].participants || []).filter(
            (id) => id !== participantId
          ),
        };
      }
      return newRooms;
    });

    setParticipantRooms((prev) => {
      const updated = { ...prev };
      if (updated[participantId] === roomId) {
        delete updated[participantId];
      }
      return updated;
    });
  }, [publishBreakoutMessage]);

  const acceptJoinRequest = useCallback((roomId, participantId) => {
    addParticipantToRoom(roomId, participantId);
  }, [addParticipantToRoom]);

  const rejectJoinRequest = useCallback((roomId, participantId) => {
    setJoinRequests((prev) => {
      const newRequests = { ...prev };
      if (newRequests[roomId]) {
        newRequests[roomId] = newRequests[roomId].filter(
          (req) => req.participantId !== participantId
        );
        if (newRequests[roomId].length === 0) {
          delete newRequests[roomId];
        }
      }
      return newRequests;
    });
  }, []);

  // Participant functions
  const requestJoinRoom = useCallback((roomId) => {
    const participantName = mMeeting?.localParticipant?.displayName || "Participant";
    publishBreakoutMessage(
      JSON.stringify({
        type: "requestJoin",
        payload: { roomId, participantName },
      })
    );

    setJoinRequests((prev) => {
      const newRequests = { ...prev };
      if (!newRequests[roomId]) {
        newRequests[roomId] = [];
      }
      const existingIndex = newRequests[roomId].findIndex(
        (req) => req.participantId === localParticipantId
      );
      if (existingIndex === -1) {
        newRequests[roomId].push({
          participantId: localParticipantId,
          participantName,
          requestedAt: new Date().getTime(),
        });
      }
      return newRequests;
    });
  }, [publishBreakoutMessage, mMeeting, localParticipantId]);

  const leaveRoom = useCallback(() => {
    if (currentRoom) {
      // Remove participant from room (this will broadcast to all participants)
      removeParticipantFromRoom(currentRoom, localParticipantId);
      
      // Immediately set currentRoom to null for local participant so they return to main meeting view
      setCurrentRoom(null);
      
      // Immediately remove from participantRooms for local participant
      setParticipantRooms((prev) => {
        const updated = { ...prev };
        if (updated[localParticipantId] === currentRoom) {
          delete updated[localParticipantId];
        }
        return updated;
      });
    }
  }, [currentRoom, localParticipantId, removeParticipantFromRoom]);

  // Check if participant is assigned to any breakout room
  const isParticipantInAnyRoom = useCallback((participantId) => {
    return !!participantRooms[participantId];
  }, [participantRooms]);

  // Check if participant is in the same room as local participant
  const isInSameRoom = useCallback((participantId) => {
    if (!currentRoom) {
      // If viewing main meeting (no room), hide participants assigned to any room
      // Only show participants who are NOT assigned to any room
      return !participantRooms[participantId];
    }
    // If in a breakout room, only show participants in the same room
    const participantRoom = participantRooms[participantId];
    return participantRoom === currentRoom;
  }, [currentRoom, participantRooms]);

  // Get all participants in the same room
  const getRoomParticipants = useCallback((roomId) => {
    return rooms[roomId]?.participants || [];
  }, [rooms]);

  const value = {
    // State
    rooms,
    participantRooms,
    joinRequests,
    currentRoom,

    // Host functions
    createRoom,
    deleteRoom,
    addParticipantToRoom,
    removeParticipantFromRoom,
    acceptJoinRequest,
    rejectJoinRequest,

    // Participant functions
    requestJoinRoom,
    leaveRoom,

    // Utility functions
    isInSameRoom,
    isParticipantInAnyRoom,
    getRoomParticipants,
  };

  return (
    <BreakoutRoomsContext.Provider value={value}>
      {children}
    </BreakoutRoomsContext.Provider>
  );
};
